import {
  Menu,
  Item,
  Option,
  TranslationItemType,
  TranslationItemField,
  Category,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { INGREDIENTS_FIELD_MAX_LENGTH, PRODUCT_INFO_FIELD_MAX_LENGTH } from 'modules/menu/constants';

import { TranslationSection, TranslationItemSubSection, ProductInfoField, TranslationSectionItem } from './types';

export const buildSectionItemKey = (item: TranslationSectionItem) => `${item.itemType}.${item.field}.${item.itemId}`;

export const buildTranslationKey = (
  itemType: string,
  field: TranslationItemField,
  itemId: string,
  language: Language,
): string => `${itemType}.${field}.${itemId}.${language}`;

export const hasDescription = (itemType: TranslationItemType): itemType is 'item' | 'category' => {
  return (['item', 'category'] as TranslationItemType[]).includes(itemType);
};

const flattenCategories = (categories: Category[]) =>
  categories.flatMap(category => [category, ...category.child_categories]);

const localiser = (language: Language) => (localised: Localization[]) => {
  const localisation = localised.find(({ lang }) => lang === language);
  return localisation ? localisation.value : null;
};

const PRODUCT_INFO_FIELDS: ProductInfoField[] = [
  'ingredients',
  'additives',
  'nutrition_facts',
  'allergens',
  'producer_information',
  'distributor_information',
];

const transformItem =
  (primaryLanguage: Language) =>
  (item: Item): TranslationItemSubSection => {
    const l = localiser(primaryLanguage);
    const namePrimary = l(item.name) || '';
    const descriptionPrimary = l(item.description) || '';
    const section: TranslationItemSubSection = {
      label: namePrimary,
      price: item.baseprice,
      items: [],
    };

    if (namePrimary) {
      section.items.push({
        itemType: 'item',
        field: 'name',
        itemId: item.id,
        parentId: null,
        translations: item.name,
      });
    }

    if (descriptionPrimary) {
      section.items.push({
        itemType: 'item',
        field: 'description',
        itemId: item.id,
        parentId: null,
        translations: item.description,
      });
    }

    PRODUCT_INFO_FIELDS.filter(fieldName => item[fieldName].find(({ lang }) => lang === primaryLanguage)).forEach(
      fieldName => {
        section.items.push({
          itemType: 'item',
          field: fieldName,
          itemId: item.id,
          parentId: null,
          translations: item[fieldName],
        });
      },
    );

    item.option_bindings.forEach(optionBinding => {
      if (optionBinding.name) {
        const optionBindingNamePrimary = l(optionBinding.name) || '';
        if (optionBindingNamePrimary) {
          section.items.push({
            itemType: 'item_option',
            field: 'name',
            itemId: optionBinding.id,
            parentId: item.id,
            translations: optionBinding.name,
          });
        }
      }
    });

    return section;
  };

const transformOption =
  (primaryLanguage: Language) =>
  (option: Option): TranslationItemSubSection => {
    const l = localiser(primaryLanguage);
    const namePrimary = l(option.name) || '';
    const section: TranslationItemSubSection = {
      label: namePrimary,
      price: null,
      items: [],
    };

    if (namePrimary) {
      section.items.push({
        itemType: 'option',
        field: 'name',
        itemId: option.id,
        parentId: null,
        translations: option.name,
      });
    }

    option.values.forEach(optionValue => {
      const optionValueNamePrimary = l(optionValue.name) || '';
      if (optionValueNamePrimary) {
        section.items.push({
          itemType: 'option_value',
          field: 'name',
          itemId: optionValue.id,
          parentId: option.id,
          translations: optionValue.name,
        });
        PRODUCT_INFO_FIELDS.filter(fieldName =>
          optionValue[fieldName].find(({ lang }) => lang === primaryLanguage),
        ).forEach(fieldName => {
          section.items.push({
            itemType: 'option_value',
            field: fieldName,
            itemId: optionValue.id,
            parentId: option.id,
            translations: optionValue[fieldName],
          });
        });
      }
    });

    return section;
  };

export const buildSections = (menu: Menu, items: Item[], options: Option[]): TranslationSection[] => {
  const l = localiser(menu.primary_language);
  const categorySections: TranslationSection[] = flattenCategories(menu.categories).map(category => {
    const categoryItem: TranslationItemSubSection = {
      label: '',
      price: null,
      items: [],
    };
    const categoryNamePrimary = l(category.name) || '';
    if (categoryNamePrimary) {
      categoryItem.items.push({
        itemType: 'category',
        field: 'name',
        itemId: category.id,
        parentId: null,
        translations: category.name,
      });
    }
    const categoryDescriptionPrimary = l(category.description) || '';
    if (categoryDescriptionPrimary) {
      categoryItem.items.push({
        itemType: 'category',
        field: 'description',
        itemId: category.id,
        parentId: null,
        translations: category.description,
      });
    }

    const menuItemsInCategory = category.item_bindings
      .map(({ item_id }) => items.find(item => item.id === item_id))
      .filter((item): item is Item => !!item);

    const menuItemItems: TranslationItemSubSection[] = menuItemsInCategory.map(transformItem(menu.primary_language));

    return {
      label: categoryNamePrimary,
      subsections: [categoryItem, ...menuItemItems],
    };
  });

  const noCategoryItemSection = {
    label: 'No category',
    subsections: items
      .filter(
        item =>
          !flattenCategories(menu.categories).some(
            category => !!category.item_bindings.find(({ item_id }) => item.id === item_id),
          ),
      )
      .map(transformItem(menu.primary_language)),
  };

  const optionSection = {
    label: 'Options',
    subsections: options.map(transformOption(menu.primary_language)),
  };

  return [...categorySections, noCategoryItemSection, optionSection].filter(section => section.subsections.length > 0);
};

export const filterUntranslated = (languages: Language[], sections: TranslationSection[]): TranslationSection[] => {
  return sections
    .map((section): TranslationSection => {
      return {
        ...section,
        subsections: section.subsections.filter(subsection =>
          subsection.items.some(item =>
            languages.some(language => {
              const translation = item.translations.find(t => t.lang === language);
              return !translation || !translation.value || !translation.verified;
            }),
          ),
        ),
      };
    })
    .filter(section => section.subsections.length > 0);
};

export const findRootElement = (
  itemType: TranslationItemType,
  itemId: string,
  parentId: string | null,
  menu: Menu,
  items: Item[],
  options: Option[],
) => {
  switch (itemType) {
    case 'category':
      return menu;
    case 'item':
      return items.find(it => it.id === itemId);
    case 'item_option':
      return items.find(it => it.id === parentId);
    case 'option':
      return options.find(op => op.id === itemId);
    case 'option_value':
      return options.find(op => op.id === parentId);
    default:
      throw new Error(`Unknown \`itemType\`: \`${itemType}\``);
  }
};

export const findMenuElement = (
  itemType: TranslationItemType,
  itemId: string,
  parentId: string | null,
  menu: Menu,
  items: Item[],
  options: Option[],
) => {
  switch (itemType) {
    case 'category':
      return flattenCategories(menu.categories).find(category => category.id === itemId);
    case 'item':
      return items.find(it => it.id === itemId);
    case 'item_option':
      const item = items.find(it => it.id === parentId);
      return item && item.option_bindings.find(op => op.id === itemId);
    case 'option':
      return options.find(op => op.id === itemId);
    case 'option_value':
      const option = options.find(op => op.id === parentId);
      return option && option.values.find(ov => ov.id === itemId);
    default:
      throw new Error(`Unknown \`itemType\`: \`${itemType}\``);
  }
};

export const getNameMaxLength = (itemType: TranslationItemType): number => {
  switch (itemType) {
    case 'item':
      return 80;
    case 'option':
    case 'item_option':
      return 50;
    case 'option_value':
      return 80;
    case 'category':
      return 50;
    default:
      throw new Error(`Unknown item type \`${itemType}\``);
  }
};

export const getDescriptionMaxLength = (itemType: 'item' | 'category'): number => {
  switch (itemType) {
    case 'item':
      return 2000;
    case 'category':
      return 1000;
    default:
      throw new Error(`Unknown item type \`${itemType}\``);
  }
};

export const isProductInfoField = (field: any): field is ProductInfoField => PRODUCT_INFO_FIELDS.includes(field);

export const getFieldMaxLength = (itemType: TranslationItemType, field: TranslationItemField): number => {
  if (field === 'name') {
    return getNameMaxLength(itemType);
  }

  if (field === 'description' && hasDescription(itemType)) {
    return getDescriptionMaxLength(itemType);
  }

  if (field === 'ingredients') {
    return INGREDIENTS_FIELD_MAX_LENGTH;
  }

  if (isProductInfoField(field)) {
    return PRODUCT_INFO_FIELD_MAX_LENGTH;
  }

  throw new Error(`Unknown field \`${field}\` for item type \`${itemType}\``);
};
